import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { computed, inject, Injectable, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, switchMap } from 'rxjs';

const breakpoints = [
  '(max-width: 640px)', // sm
  '(max-width: 768px)', // md
  '(max-width: 1024px)', // lg
  '(max-width: 1280px)', // xl
  '(max-width: 1536px)' // 2xl
];

@Injectable({ providedIn: 'root' })
export class SiteStore {
  public isOffline = signal(false);
  public loading = signal(false);
  public showQrScanner = signal(false);
  public routeMetadata = signal<any>({});
  public loadingDescription = signal<string>('');
  public openDrawer = signal(false);
  public $bp = signal({
    sm: false,
    md: false,
    lg: false,
    xl: false,
    '2xl': false,
    ltmd: false,
    ltlg: false,
    ltxl: false,
    lt2xl: false,
    gtmd: false,
    gtlg: false,
    gtxl: false,
    gt2xl: false
  });
  public $sw = signal<number>(window.innerWidth);
  public $sh = signal<number>(window.innerHeight);

  isMobile = computed(() => {
    return this.$bp().sm;
  });
  isNotMobile = computed(() => {
    return !this.$bp().sm;
  });

  constructor(
    private breakpointObserver: BreakpointObserver,
    private route: ActivatedRoute,
    private router: Router
  ) {
    window.addEventListener('offline', () => {
      this.isOffline.set(true);
    });

    window.addEventListener('online', () => {
      this.isOffline.set(false);
    });

    window.addEventListener('resize', () => {
      this.$sw.set(window.innerWidth); // Update signal on resize
      this.$sh.set(window.innerHeight); // Update signal on resize
    });

    this.breakpointObserver.observe(breakpoints).subscribe((result: BreakpointState) => {
      const sm = result.breakpoints['(max-width: 640px)'];
      const md = result.breakpoints['(max-width: 768px)'];
      const lg = result.breakpoints['(max-width: 1024px)'];
      const xl = result.breakpoints['(max-width: 1280px)'];
      const xl2 = result.breakpoints['(max-width: 1536px)'];

      this.$bp.set({
        sm,
        md: !sm && md,
        lg: !md && lg,
        xl: !lg && xl,
        '2xl': !xl && xl2,
        ltmd: sm,
        ltlg: sm || md,
        ltxl: sm || md || lg,
        lt2xl: sm || md || lg || xl,
        gtmd: !sm,
        gtlg: !sm && !md,
        gtxl: !sm && !md && !lg,
        gt2xl: !sm && !md && !lg && !xl
      });
    });

    this.extractMetadata();
  }

  setLoading(description?: string) {
    if (description) {
      this.loadingDescription.set(description);
    }

    this.loading.set(true);
  }

  removeLoading() {
    this.loading.set(false);
    this.loadingDescription.set('');
  }

  extractMetadata() {
    this.route.data.subscribe((data) => {
      this.routeMetadata.set(data);
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        switchMap(() => {
          let child = this.route.firstChild;
          while (child?.firstChild) {
            child = child.firstChild;
          }
          return child?.data ?? this.route.data;
        })
      )
      .subscribe((data) => {
        this.routeMetadata.set(data);
      });
  }

  toggleShowQrScanner() {
    this.showQrScanner.update((value) => !value);
  }
}
